/**
 * SeedDrive V2 — Role Context
 *
 * Global role state for the prototype.
 * Replaces prop-drilling of userRole / onRoleChange through AppLayout.
 *
 * Usage:
 *   import { useRole } from 'context/RoleContext';
 *   const { role, setRole, profile, permissions, isInternal, isExternal } = useRole();
 *   const { activeContext, availableContexts, setContext } = useRole();
 *
 * Prototype constraint: role is mock state only. No auth enforcement.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  DEFAULT_ROLE,
  ROLE_PROFILES,
  ROLE_MODULE_PERMISSIONS,
  ROLE_NAV_PERMISSIONS,
  isInternalRole,
  isExternalRole,
  getRoleProfile,
  DEMO_CONTEXTS,
  DEFAULT_CONTEXT_ID,
} from 'config/roleConfig';

const RoleContext = createContext(null);

function resolveContext(idOrNull) {
  const id = idOrNull || DEFAULT_CONTEXT_ID;
  return DEMO_CONTEXTS.find((c) => c.id === id) || DEMO_CONTEXTS[0];
}

export function RoleProvider({ children }) {
  const [activeContext, setActiveContextState] = useState(() => {
    const saved = sessionStorage.getItem('sd_demo_context');
    return resolveContext(saved);
  });

  // Primary setter — switches by context id
  const setContext = useCallback((id) => {
    const ctx = resolveContext(id);
    setActiveContextState(ctx);
    sessionStorage.setItem('sd_demo_context', ctx.id);
  }, []);

  // Backwards-compat wrapper — finds first context whose role matches
  const setRole = useCallback((roleValue) => {
    const ctx = DEMO_CONTEXTS.find((c) => c.role === roleValue) || DEMO_CONTEXTS[0];
    setActiveContextState(ctx);
    sessionStorage.setItem('sd_demo_context', ctx.id);
  }, []);

  const role = activeContext.role;

  const value = useMemo(() => ({
    // Context switcher API
    activeContext,
    availableContexts: DEMO_CONTEXTS,
    setContext,
    // Role API (unchanged for all existing consumers)
    role,
    setRole,
    profile:     getRoleProfile(role),
    permissions: ROLE_MODULE_PERMISSIONS[role] ?? {},
    navAccess:   ROLE_NAV_PERMISSIONS[role] ?? [],
    isInternal:  isInternalRole(role),
    isExternal:  isExternalRole(role),
    can: (action) => !!(ROLE_MODULE_PERMISSIONS[role]?.[action]),
    hasNav: (sectionId) => (ROLE_NAV_PERMISSIONS[role] ?? []).includes(sectionId),
  }), [activeContext, role, setContext, setRole]);

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error('useRole must be used inside <RoleProvider>');
  }
  return ctx;
}

// Re-export ROLE_PROFILES so consumers can build role switchers without an extra import
export { ROLE_PROFILES };
